import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MultiSelect } from '~/components/multi-select';
import { cn } from '@/lib/utils';
import { OP_TYPE_RANGE, operandById, orderedValueOperands } from '~/lib/filters/operands';
import { useCustomerGenders, useCustomersByIds, lookupCustomers } from '~/hooks/use-customer-groups';
import { useCustomerTiersList } from '~/hooks/use-customer-tiers';
import { useCustomerCategoriesList } from '~/hooks/use-customer-categories';

/**
 * Customer-group dynamic criteria builder — admin-v2 surface for the legacy `dynamicFilters`
 * registry restricted to `resources: ['customers']`
 * (Core/client/admin/src/dynamic-filters/index.js + Core/server/.../Criteria/Customers/*).
 *
 * Persisted `$type` discriminators (verified against the `[CriteriaType("…")]` backend attributes):
 * compounds `and`/`or`/`not`; leaves `customerAge` {From,To}, `customerGender` {ids:Gender[]},
 * `customerTier` (ordered {op,value|from,to} of tier ids), `customerId` {ids:int[]},
 * `customerCategory` {ids:int[]} (exact category ids, children not expanded).
 * `customerOrderStats` (and any other/unknown leaf) round-trips losslessly as a preserved "advanced"
 * node — mirroring the product filter builder's `unsupported` pattern — rather than being dropped.
 */

const AND = 'and';
const OR = 'or';
const NOT = 'not';

export interface CriteriaNode {
	$type?: string;
	criteria?: CriteriaNode | CriteriaNode[];
	op?: string;
	value?: unknown;
	from?: unknown;
	to?: unknown;
	From?: unknown;
	To?: unknown;
	ids?: number[];
	[key: string]: unknown;
}

const COMPOUNDS = [
	{ type: AND, label: 'AND', unary: false },
	{ type: OR, label: 'ANY', unary: false },
	{ type: NOT, label: 'NOT', unary: true }
];

// Leaves with a native editor. `customerOrderStats` is intentionally absent → preserved/advanced.
const EDITABLE_LEAVES = ['customerTier', 'customerGender', 'customerAge', 'customerId', 'customerCategory'] as const;
const COMPOUND_TYPES = new Set([AND, OR, NOT]);

/* --------------------------------------------------------------- leaf editors */

function TierLeaf({ value, onChange }: { value: CriteriaNode; onChange: (v: CriteriaNode) => void }) {
	const { t } = useTranslation();
	const { data: tiers } = useCustomerTiersList();
	const operand = operandById(value.op as string | undefined);
	const isRange = operand?.type === OP_TYPE_RANGE;
	const setOp = (op: string) => {
		const { value: _v, from: _f, to: _t, ...rest } = value;
		onChange({ ...rest, op });
	};
	const tierSelect = (field: 'value' | 'from' | 'to') => (
		<Select value={value[field] != null ? String(value[field]) : undefined} onValueChange={v => onChange({ ...value, [field]: Number(v) })}>
			<SelectTrigger className="h-9 w-44">
				<SelectValue placeholder={t('customers.groups.criteria.tier')} />
			</SelectTrigger>
			<SelectContent>
				{(tiers ?? []).map(tr => (
					<SelectItem key={tr.id} value={String(tr.id)}>
						{tr.nm}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
	return (
		<div className="flex flex-wrap items-center gap-2">
			<Select value={(value.op as string) ?? undefined} onValueChange={setOp}>
				<SelectTrigger className="h-9 w-auto min-w-24">
					<SelectValue placeholder={t('customers.groups.criteria.operand')} />
				</SelectTrigger>
				<SelectContent>
					{orderedValueOperands.map(op => (
						<SelectItem key={op.id} value={op.id}>
							{op.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{value.op &&
				(isRange ? (
					<>
						{tierSelect('from')}
						{tierSelect('to')}
					</>
				) : (
					tierSelect('value')
				))}
		</div>
	);
}

function GenderLeaf({ value, onChange }: { value: CriteriaNode; onChange: (v: CriteriaNode) => void }) {
	const { t } = useTranslation();
	const { data: genders } = useCustomerGenders();
	const options = (genders ?? []).map(g => ({ id: g.id as number, nm: g.nm ?? '' })).filter(o => o.id != null);
	const ids = value.ids ?? [];
	const selected = options.filter(o => ids.includes(o.id));
	return (
		<div className="min-w-52 flex-1">
			<MultiSelect
				value={selected}
				onChange={next => onChange({ ...value, ids: next.map(o => o.id) })}
				options={options}
				getKey={o => String(o.id)}
				getLabel={o => o.nm}
				placeholder={t('customers.groups.criteria.selectGenders')}
			/>
		</div>
	);
}

function CategoryLeaf({ value, onChange }: { value: CriteriaNode; onChange: (v: CriteriaNode) => void }) {
	const { t } = useTranslation();
	const { data: categories } = useCustomerCategoriesList();
	const options = (categories ?? []).map(c => ({ id: c.id as number, nm: c.nm ?? '' })).filter(o => o.id != null);
	const ids = value.ids ?? [];
	const selected = options.filter(o => ids.includes(o.id));
	return (
		<div className="min-w-52 flex-1">
			<MultiSelect
				value={selected}
				onChange={next => onChange({ ...value, ids: next.map(o => o.id) })}
				options={options}
				getKey={o => String(o.id)}
				getLabel={o => o.nm}
				placeholder={t('customers.groups.criteria.selectCategories')}
			/>
		</div>
	);
}

function AgeLeaf({ value, onChange }: { value: CriteriaNode; onChange: (v: CriteriaNode) => void }) {
	const { t } = useTranslation();
	const num = (v: unknown) => (v === '' || v == null ? undefined : Number(v));
	return (
		<div className="flex flex-wrap items-center gap-2">
			<Input
				type="number"
				min={0}
				className="h-9 w-28"
				placeholder={t('customers.groups.criteria.ageFrom')}
				value={(value.From as number | undefined) ?? ''}
				onChange={e => onChange({ ...value, From: num(e.target.value) })}
			/>
			<Input
				type="number"
				min={0}
				className="h-9 w-28"
				placeholder={t('customers.groups.criteria.ageTo')}
				value={(value.To as number | undefined) ?? ''}
				onChange={e => onChange({ ...value, To: num(e.target.value) })}
			/>
		</div>
	);
}

interface CustomerOpt {
	id: number;
	label: string;
}
const customerLabel = (c: { fn?: string | null; ln?: string | null; eml?: string; id?: number }) =>
	[c.fn, c.ln].filter(Boolean).join(' ').trim() || c.eml || `#${c.id}`;

function CustomerIdLeaf({ value, onChange }: { value: CriteriaNode; onChange: (v: CriteriaNode) => void }) {
	const { t } = useTranslation();
	const ids = value.ids ?? [];
	const { data: hydrated } = useCustomersByIds(ids);
	const selected: CustomerOpt[] = ids.map(id => {
		const c = hydrated?.find(h => h.id === id);
		return { id, label: c ? customerLabel(c) : `#${id}` };
	});
	return (
		<div className="min-w-64 flex-1">
			<MultiSelect<CustomerOpt>
				value={selected}
				onChange={next => onChange({ ...value, ids: next.map(o => o.id) })}
				options={[]}
				loadOptions={async q => {
					const res = await lookupCustomers(q);
					return res.filter(c => c.id != null).map(c => ({ id: c.id as number, label: customerLabel(c) }));
				}}
				getKey={o => String(o.id)}
				getLabel={o => o.label}
				placeholder={t('customers.groups.criteria.selectCustomers')}
			/>
		</div>
	);
}

function LeafEditor({ value, onChange }: { value: CriteriaNode; onChange: (v: CriteriaNode) => void }) {
	const { t } = useTranslation();
	switch (value.$type) {
		case 'customerTier':
			return <TierLeaf value={value} onChange={onChange} />;
		case 'customerGender':
			return <GenderLeaf value={value} onChange={onChange} />;
		case 'customerAge':
			return <AgeLeaf value={value} onChange={onChange} />;
		case 'customerId':
			return <CustomerIdLeaf value={value} onChange={onChange} />;
		case 'customerCategory':
			return <CategoryLeaf value={value} onChange={onChange} />;
		default:
			return <span className="text-xs text-foreground-subtle italic">{t('customers.groups.criteria.advancedPreserved')}</span>;
	}
}

/* --------------------------------------------------------------- type selector */

function leafLabelKey(type: string) {
	return `customers.groups.criteria.types.${type}`;
}

function TypeSelect({ value, onChange }: { value?: string; onChange: (type: string) => void }) {
	const { t } = useTranslation();
	// Preserve an unknown/advanced leaf as a selectable (disabled-looking) option so it round-trips.
	const isAdvanced = value && !COMPOUND_TYPES.has(value) && !EDITABLE_LEAVES.includes(value as (typeof EDITABLE_LEAVES)[number]);
	return (
		<Select value={value ?? undefined} onValueChange={onChange}>
			<SelectTrigger className="h-9 w-56">
				<SelectValue placeholder={t('customers.groups.criteria.selectFilter')} />
			</SelectTrigger>
			<SelectContent>
				<SelectGroup>
					<SelectLabel>{t('customers.groups.criteria.combine')}</SelectLabel>
					{COMPOUNDS.map(f => (
						<SelectItem key={f.type} value={f.type}>
							{f.label}
						</SelectItem>
					))}
				</SelectGroup>
				<SelectGroup>
					<SelectLabel>{t('customers.groups.criteria.conditions')}</SelectLabel>
					{EDITABLE_LEAVES.map(type => (
						<SelectItem key={type} value={type}>
							{t(leafLabelKey(type))}
						</SelectItem>
					))}
					{isAdvanced && (
						<SelectItem value={value} disabled>
							{t(leafLabelKey(value), { defaultValue: value })} · {t('customers.groups.criteria.advanced')}
						</SelectItem>
					)}
				</SelectGroup>
			</SelectContent>
		</Select>
	);
}

/* --------------------------------------------------------------- recursive node */

interface NodeProps {
	value: CriteriaNode;
	onChange: (v: CriteriaNode) => void;
	onRemove?: () => void;
	depth?: number;
}

function CriteriaNodeEditor({ value, onChange, onRemove, depth = 0 }: NodeProps) {
	const { t } = useTranslation();
	const type = value?.$type;
	const compound = COMPOUNDS.find(c => c.type === type);

	const changeType = (newType: string) => {
		if (!newType || newType === type) return;
		const target = COMPOUNDS.find(c => c.type === newType);
		const hasType = Boolean(type);
		if (target && !target.unary) onChange({ $type: newType, criteria: hasType ? [value] : [{}] });
		else if (target && target.unary) onChange({ $type: newType, criteria: hasType ? value : {} });
		else onChange({ $type: newType });
	};

	if (compound && !compound.unary) {
		const children = Array.isArray(value.criteria) ? value.criteria : [];
		const setChildren = (next: CriteriaNode[]) => onChange({ $type: value.$type, criteria: next });
		return (
			<div className={cn('rounded-md border border-border bg-elevated/40 p-3', depth > 0 && 'mt-1')}>
				<div className="flex items-center gap-2">
					<Button type="button" variant="outline" size="sm" className="h-7 font-semibold" onClick={() => onChange(children[0] ?? {})}>
						{compound.label}
					</Button>
					{onRemove && (
						<Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-danger-fg" onClick={onRemove}>
							<X size={14} />
						</Button>
					)}
				</div>
				<div className="mt-2 flex flex-col gap-2 border-l-2 border-border pl-4">
					{children.map((child, i) => (
						<CriteriaNodeEditor
							key={i}
							value={child}
							depth={depth + 1}
							onChange={next => setChildren(children.map((c, j) => (j === i ? next : c)))}
							onRemove={() => setChildren(children.filter((_, j) => j !== i))}
						/>
					))}
					<div>
						<Button type="button" variant="outline" size="sm" className="h-7" onClick={() => setChildren([...children, {}])}>
							<Plus size={14} /> {t('customers.groups.criteria.addCondition')}
						</Button>
					</div>
				</div>
			</div>
		);
	}

	if (compound && compound.unary) {
		const child = (Array.isArray(value.criteria) ? value.criteria[0] : value.criteria) ?? {};
		return (
			<div className={cn('rounded-md border border-border bg-elevated/40 p-3', depth > 0 && 'mt-1')}>
				<div className="flex items-center gap-2">
					<Button type="button" variant="outline" size="sm" className="h-7 font-semibold" onClick={() => onChange(child)}>
						{compound.label}
					</Button>
					{onRemove && (
						<Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-danger-fg" onClick={onRemove}>
							<X size={14} />
						</Button>
					)}
				</div>
				<div className="mt-2 border-l-2 border-border pl-4">
					<CriteriaNodeEditor value={child} depth={depth + 1} onChange={next => onChange({ $type: NOT, criteria: next })} />
				</div>
			</div>
		);
	}

	return (
		<div className="flex flex-wrap items-center gap-2">
			<TypeSelect value={value?.$type} onChange={changeType} />
			{value?.$type && !compound && <LeafEditor value={value} onChange={onChange} />}
			{onRemove && (
				<Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-danger-fg" onClick={onRemove}>
					<X size={14} />
				</Button>
			)}
		</div>
	);
}

/** Root of the customer-group criteria builder. `value` is the persisted expression JSON ({} when empty). */
export function CriteriaBuilder({ value, onChange }: { value: CriteriaNode; onChange: (v: CriteriaNode) => void }) {
	return <CriteriaNodeEditor value={value ?? {}} onChange={onChange} />;
}
